import { z } from 'zod';
import { DEFAULT_SUMMARY_METADATA, type SummaryMetadata } from '@/components/shared/link-preview/types';
import { consumeSummarizerStream } from '@/lib/ai/streaming';
import { extractCompletionUpdate, extractStatusMetadata, extractTokenUpdate } from './extractors';
import { requestSummarizeStream, type SummarizeRequestBody } from './fetch';
import type { StreamLinkSummaryArguments } from './types';

const SummarizerErrorSchema = z
  .object({
    message: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();

export async function streamLinkSummary({
  url,
  controller,
  setSummaryState,
  isMountedRef,
  summaryLength,
  model,
  shareContext,
}: StreamLinkSummaryArguments): Promise<void> {
  const requestBody: SummarizeRequestBody = { url };
  if (summaryLength) requestBody.summaryLength = summaryLength;
  if (model) requestBody.model = model;
  if (Array.isArray(shareContext) && shareContext.length > 0) {
    requestBody.shareContext = shareContext;
  }

  const response = await requestSummarizeStream({ signal: controller.signal, body: requestBody });

  const stream = response.body;
  if (!stream) {
    setSummaryState((previous) => ({
      ...previous,
      status: 'error',
      error: 'Unable to summarize link.',
    }));
    return;
  }

  const initialMetadata: SummaryMetadata = { ...DEFAULT_SUMMARY_METADATA, summaryLength: summaryLength ?? null };

  await consumeSummarizerStream({
    stream,
    controller,
    isMountedRef,
    setSummaryState,
    initialMetadata,
    defaultErrorMessage: 'Unable to summarize link.',
    handlers: {
      onStatus: (payload, metadata) => extractStatusMetadata(payload, metadata),
      onToken: (payload, metadata, context) => extractTokenUpdate(payload, metadata, context),
      onCompletion: (payload, metadata, aggregated) => extractCompletionUpdate(payload, metadata, aggregated),
      onErrorMessage: (payload) => {
        const parsed = SummarizerErrorSchema.safeParse(payload);
        const message = parsed.success ? parsed.data.message : null;
        return message && message.length > 0 ? message : null;
      },
    },
  });
}
